<template>
  <a-config-provider :locale="locale">
    <router-view />
    <global-setting />
  </a-config-provider>
</template>

<script lang="ts" setup>
  import { computed, defineComponent, onMounted, watch } from 'vue';
  import enUS from '@arco-design/web-vue/es/locale/lang/en-us';
  import zhCN from '@arco-design/web-vue/es/locale/lang/zh-cn';
  import GlobalSetting from '@/components/global-setting/index.vue';
  import useLocale from '@/hooks/locale';
  import { useAppStore, useUserStore } from '@/store';
  import { useRouter } from 'vue-router';
  import { Message } from '@arco-design/web-vue';

  const { currentLocale } = useLocale();
  const locale = computed(() => {
    switch (currentLocale.value) {
      case 'zh-CN':
        return zhCN;
      case 'en-US':
        return enUS;
      default:
        return enUS;
    }
  });

  const appStore = useAppStore();
  const userStore = useUserStore();
  const router = useRouter();

  const initApp = async () => {
    const token = localStorage.getItem('token');
    if (token && !appStore.menuFromServer) {
      console.log('App初始化: 检测到token但没有菜单数据，开始获取菜单');
      try {
        await appStore.fetchServerMenuConfig();
      } catch (error) {
        console.error('App初始化: 获取菜单失败', error);
        Message.error('获取菜单数据失败，请重新登录');
        router.push({ name: 'login' });
      }
    }

    // 检查用户是否有未结算的电费账单
    if (token && userStore.hasUnsettledElectricityBills) {
      console.log('App初始化: 检测到未结算电费账单，显示提示');
      // 延迟显示提示，确保页面已加载完成
      setTimeout(() => {
        userStore.showUnsettledBillsNotification();
      }, 2000);
    }
  };

  // 设置路由监听器的函数
  const setupRouteWatcher = () => {
    console.log('设置路由监听器，当前用户状态:', { 
      hasUnsettledBills: userStore.hasUnsettledElectricityBills,
      currentPath: router.currentRoute.value.path 
    });
    
    // 监听路由变化，当用户切换到其他页面时检查是否需要显示电费账单提醒
    watch(
      () => router.currentRoute.value.path,
      (newPath, oldPath) => {
        console.log('路由变化监听:', { newPath, oldPath, hasUnsettledBills: userStore.hasUnsettledElectricityBills });
        
        // 如果用户有未结算电费账单，且页面发生变化
        if (userStore.hasUnsettledElectricityBills && oldPath && newPath !== oldPath) {
          // 检查是否切换到费用与账单页面
          const isToBillingPage = newPath.includes('/billing/electricity');
          
          console.log('页面切换检查:', { isToBillingPage });
          
          // 如果切换到非费用与账单页面，显示提醒
          if (!isToBillingPage) {
            console.log('用户切换到非费用与账单页面，显示提醒');
            // 延迟显示提示，避免页面切换时的冲突
            setTimeout(() => {
              userStore.showUnsettledBillsNotification();
            }, 1000);
          } else {
            console.log('用户切换到费用与账单页面，不显示提醒');
          }
        } else {
          console.log('不满足显示条件:', { 
            hasUnsettledBills: userStore.hasUnsettledElectricityBills, 
            oldPath, 
            newPath, 
            isDifferent: newPath !== oldPath 
          });
        }
      }
    );
  };

  onMounted(() => {
    initApp();
    // 延迟设置路由监听器，确保用户信息已加载
    setTimeout(() => {
      setupRouteWatcher();
    }, 3000);
  });
</script>

